import { action, computed, makeObservable, observable } from 'mobx';

import type { DrugData } from 'chaika-frontend-types/frontend-cms';
import { DrugStatus, Locale } from 'chaika-enums';

import { DateHelper, DateType } from '@utils/DateHelper';
import { assignParams } from '@utils/assignParams';
import { translate } from '@chaika/i18n/helper';
import { DrugField } from '@store/models/EntryFields';

export class Drug {
    @observable public readonly id!: string;
    @observable public readonly ehrId!: string;
    @observable public readonly authorId!: string;
    @observable public readonly parentEntryId!: string;
    @observable public readonly createdAt!: string;
    @observable public status!: DrugStatus;
    @observable public activeSubstance!: string;
    @observable public tradeNameIds?: string[];
    @observable public customTradeNames?: string[];
    @observable public medicalCaseIds: string[] = [];
    @observable public frequencyIfReception!: string;
    @observable public reasonForCancellation!: string;
    @observable public directions?: string;
    @observable public start?: string;
    @observable public end?: string;
    @observable public cancelledAt?: string;
    @observable public cancelledBy?: string;

    constructor(params: DrugData) {
        makeObservable(this);
        assignParams<DrugData>(this, params);
    }

    @computed
    public get isActive(): boolean {
        return this.status === DrugStatus.Active;
    }

    @computed
    public get isCancelled(): boolean {
        return (
            this.status === DrugStatus.Cancelled ||
            this.status === DrugStatus.CancelledAppointment ||
            this.status === DrugStatus.CanceledByPatient
        );
    }

    @computed
    public get endString(): string {
        return this.end
            ? `${translate('to')} ${DateHelper.formatDateWithYearAwareness(this.end)}`
            : translate('indefinitely');
    }

    @computed
    public get startEndString(): string {
        const startString = this.start
            ? `${translate('fromSecond')} ${DateHelper.formatDateWithYearAwareness(this.start)}`
            : undefined;
        const endString = `${this.end ? DateHelper.formatDateWithYearAwareness(this.end) : translate('indefinitely')}`;

        return [startString, endString].filter(Boolean).join(` ${this.end ? translate('to').toLowerCase() : ' '} `);
    }

    public get description(): string {
        const frequencyIfReception = this.frequencyIfReception;
        const activeSubstanceId = this.activeSubstance;
        const tradeNameIds = this.tradeNameIds ?? [];
        const customTradeNames = this.customTradeNames ?? [];

        const tradeNames = DrugField.getTradeNameTitles(tradeNameIds ?? [], customTradeNames ?? []);
        const activeSubstance = DrugField.getActiveSubstanceTitle(activeSubstanceId);

        const description: string[] = [];

        if (tradeNames && activeSubstance) {
            description.push(`(${activeSubstance})`);
        }

        if (frequencyIfReception) {
            description.push(frequencyIfReception);
        }

        return `${!!tradeNames ? tradeNames : activeSubstance}${
            frequencyIfReception || (activeSubstance && tradeNames) ? ` — ${description.join(' ')}` : ''
        }`;
    }

    public getCustomDescriptionForTranslations(frequencyIfReception: string, locale: Locale) {
        const tradeNames = DrugField.getTradeNameTitles(this.tradeNameIds ?? [], this.customTradeNames ?? [], locale);
        const activeSubstance = DrugField.getActiveSubstanceTitle(this.activeSubstance, locale);

        const description: string[] = [];

        if (tradeNames && activeSubstance) {
            description.push(`(${activeSubstance})`);
        }

        if (frequencyIfReception) {
            description.push(frequencyIfReception);
        }

        return `${!!tradeNames ? tradeNames : activeSubstance}${
            frequencyIfReception || (activeSubstance && tradeNames) ? ` — ${description.join(' ')}` : ''
        }`;
    }

    public getEndString(locale?: Locale): string {
        return DateHelper.makeToDateString(this.end, locale);
    }

    public isActualOnDate(compareDate: DateType = Date.now()): boolean {
        return !this.end || DateHelper.isAfter(this.end, compareDate);
    }

    public isBelongToEntry(entryId: string): boolean {
        return this.parentEntryId === entryId;
    }

    @action
    public update({
        activeSubstance,
        frequencyIfReception,
        directions,
        reasonForCancellation,
        cancelledAt,
        cancelledBy,
        start,
        end,
        medicalCaseIds,
    }: Partial<DrugData>): void {
        this.activeSubstance = activeSubstance ?? this.activeSubstance;
        this.frequencyIfReception = frequencyIfReception ?? this.frequencyIfReception;
        this.cancelledAt = cancelledAt ?? this.cancelledAt;
        this.cancelledBy = cancelledBy ?? this.cancelledBy;
        this.reasonForCancellation = reasonForCancellation ?? this.reasonForCancellation;
        this.directions = directions ?? this.directions;
        this.start = start ?? this.start;
        this.end = end ?? this.end;
        this.medicalCaseIds = medicalCaseIds ?? this.medicalCaseIds;
    }

    @action
    public removeMedicalCase(medicalCaseId: string): void {
        this.medicalCaseIds = this.medicalCaseIds.filter(id => id !== medicalCaseId);
    }

    public serialize(): DrugData {
        return {
            id: this.id,
            ehrId: this.ehrId,
            authorId: this.authorId,
            parentEntryId: this.parentEntryId,
            createdAt: this.createdAt,
            status: this.status,
            medicalCaseIds: this.medicalCaseIds,
            activeSubstance: this.activeSubstance,
            frequencyIfReception: this.frequencyIfReception,
            reasonForCancellation: this.reasonForCancellation,
            cancelledAt: this.cancelledAt,
            cancelledBy: this.cancelledBy,
            directions: this.directions,
            start: this.start,
            end: this.end,
        };
    }
}
